import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { last } from 'lodash-es';
import { AnimatePresence, MotionConfig } from 'motion/react';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useMatch, useMatches } from 'react-router';
import AppHelmet from '@/components/layouts/page-layout/app-helmet';
import AppPrompter from '@/components/layouts/page-layout/app-prompter';
import AppToast from '@/components/layouts/page-layout/app-toast';
import PwaPrompt from '@/components/pwa-prompt/pwa-prompt';
import { ScreenBreakpointIndicator } from '@/components/screen-breakpoint-indicator';
import { ThemeSwitcher } from '@/components/theme-switcher';
import useComm from '@/hooks/comm/use-comm';
import usePeriodicMessages from '@/hooks/conversations/use-periodic-messages';
import { useAutoSendTelemetry } from '@/hooks/debug/use-telemetry';
import useNotificationPermission from '@/hooks/fcm/use-notification-permission';
import useClientId from '@/hooks/pwa/use-client-id';
import { useSubscribedSessionUnReadCountByUserId } from '@/hooks/sessions/use-subscribe-sessions';
import useAppGuard from '@/hooks/use-app-guard';
import useUserPlatform, { useIsDesktop } from '@/hooks/use-user-platform';
import useWebSocketManager from '@/hooks/use-web-socket-manager';
import { useInitUserState } from '@/hooks/users/use-init-user-state';
import ROUTES, { handleSchema } from '@/routes';
import { RootLayoutContext } from '@/routes/root.context';
import { useDebugMode } from '@/stores/device';
import { useMessageStore } from '@/stores/message';
import { parseSessionKey } from '@/utils/session';

export default function RootRoute() {
  const { pathname } = useLocation();
  const matches = useMatches();
  const { isDesktop, isEmbedded } = useUserPlatform();
  const { commManager } = useComm();
  const { key } = useMessageStore();
  const [debugMode] = useDebugMode();
  const [type, id] = parseSessionKey(key);
  const matchSessionMessageRoute = useMatch({
    path: ROUTES.MESSAGES[type === 'session' ? 'SESSIONS' : 'GROUPS'].$buildPath({ params: { id } }),
    end: false,
  });

  useClientId();
  useAppGuard(!pathname.startsWith('/auth'));
  useNotificationPermission();
  usePeriodicMessages();
  useWebSocketManager();
  useSubscribedSessionUnReadCountByUserId();
  useInitUserState();
  useAutoSendTelemetry();

  const showFooter = useMemo(() => {
    if (isDesktop) return true;
    const lastHandle = handleSchema.safeParse(last(matches)?.handle);
    return lastHandle.success && Boolean(lastHandle.data.showFooter);
  }, [matches, isDesktop]);

  useEffect(() => {
    if (commManager && isEmbedded) {
      commManager?.dispatch({ event: 'fullscreen', data: { toggle: !showFooter } });
    }
  }, [isEmbedded, showFooter, commManager]);

  // Different behavior for desktop and mobile
  // On desktop, we don't reset key because key is needed for the desktop right section
  // On mobile, in order to calculate the correct undread count in messaage list page,
  // we need to reset the key making sure the key is default
  useEffect(() => {
    if (isDesktop) return;
    if (!matchSessionMessageRoute) useMessageStore.getState().resetKey();
  }, [isDesktop, matchSessionMessageRoute]);

  return (
    <RootLayoutContext.Provider value={{ showFooter }}>
      <AppHelmet />

      <div
        id='wrapper'
        className='bg-neutral-0 flex flex-col items-stretch min-h-dvh w-screen desktop:mx-auto desktop:relative 3xl:shadow-2xl 3xl:rounded-2xl desktop:max-w-screen-3xl desktop:overflow-x-hidden'>
        <PwaPrompt />

        <AnimatedOutlet />
      </div>

      <AppToast />

      <AppPrompter />

      {debugMode && (
        <>
          <ReactQueryDevtools buttonPosition='bottom-left' />
          <ScreenBreakpointIndicator />
          <ThemeSwitcher />
        </>
      )}
    </RootLayoutContext.Provider>
  );
}

export const AnimatedOutlet = () => {
  const isDesktop = useIsDesktop();

  if (isDesktop) return <Outlet />;

  return (
    <MotionConfig
      reducedMotion='user'
      transition={{ ease: 'circInOut', duration: 0.15 }}>
      <AnimatePresence
        mode='wait'
        initial={false}>
        <Outlet />
      </AnimatePresence>
    </MotionConfig>
  );
};
